import { useUiStore } from '@/stores/ui'
import { readonly, ref } from 'vue'
import { config } from '@/composables/useMqttConnection'
import { TRUSTED_PARENT_ORIGINS } from '@/constants/trusted-origins'
import { OVERVIEW_ROUTE, startRoute } from '@/circuits/circuits'

const _currentRoute = ref('/')
export const currentRoute = readonly(_currentRoute)

/** Puts the title of a route into the navbar. */
function showTitle(route: string): void {
  const cleanRoute = route.startsWith('/') ? route.slice(1) : route
  const uiStore = useUiStore()

  // Handle special routes first
  if (cleanRoute.endsWith('messagePage')) {
    uiStore.setScreenName('#messagebox')
    return
  }
  if (cleanRoute.endsWith('profilePage')) {
    uiStore.setScreenName('#profile')
    return
  }
  if (cleanRoute.endsWith('chatbotPage')) {
    uiStore.setScreenName('#chatbot')
    return
  }
  if (cleanRoute.endsWith('filemanagerPage')) {
    uiStore.setScreenName('#filemanager')
    return
  }
  if (cleanRoute.endsWith('dpviewerPage')) {
    uiStore.setScreenName('#datapoints')
    return
  }
  if (cleanRoute.endsWith('orderPage')) {
    uiStore.setScreenName('#order')
    return
  }
  if (cleanRoute.endsWith('setupWizardPage')) {
    uiStore.setScreenName('#setup_wizard')
    return
  }
  if (cleanRoute.endsWith(OVERVIEW_ROUTE)) {
    // The plant overview carries the name of the plant, as on the panel.
    uiStore.setScreenName(`*${config.value.general?.projectName ?? ''}`)
    return
  }

  // Handle regular routes
  if (config.value.frontend.screens[cleanRoute]) {
    uiStore.setScreenName(config.value.frontend.screens[cleanRoute].title || cleanRoute)
  } else {
    console.error(`No screen found for route: ${cleanRoute}`)
  }
}

function navigateTo(route: string) {
  console.log('Navigating to', route)

  // Get current query parameters
  const currentQuery = window.location.search

  // Check if this is the initial navigation to startscreen from root "/"
  const isInitialNavigation = _currentRoute.value === '/' && route !== '/'

  // Update URL without page reload, preserving query parameters
  if (isInitialNavigation) {
    // Replace the root "/" entry instead of pushing a new one to prevent back navigation
    window.history.replaceState({}, '', route + currentQuery)
  } else {
    window.history.pushState({}, '', route + currentQuery)
  }
  _currentRoute.value = route
  showTitle(route)
}

// Listen to browser back/forward buttons - once for the app, however many
// components ask for the router.
let listeningToHistory = false

function listenToHistory(): void {
  if (listeningToHistory) return
  listeningToHistory = true
  window.addEventListener('popstate', () => {
    console.log('Popstate event triggered', window.location.pathname)

    // Prevent navigation back to root "/" - redirect to startscreen instead
    if (window.location.pathname === '/') {
      console.log('Attempted navigation to root "/" - redirecting to startscreen')

      // Check if we're in an iframe context
      if (window.parent !== window) {
        // If in iframe, notify parent to handle navigation
        console.log('In iframe context - notifying parent window')
        const parentOrigin = document.referrer ? new URL(document.referrer).origin : null
        if (parentOrigin && TRUSTED_PARENT_ORIGINS.includes(parentOrigin)) {
          window.parent.postMessage(
            {
              type: 'NAVIGATE_BACK',
              reason: 'prevent_root_navigation',
            },
            parentOrigin,
          )
        }
        return
      }

      // If not in iframe, redirect to where the app opens
      const start = startRoute(config.value.systems ?? [])
      if (start) {
        navigateTo(start)
        return
      }
    }

    _currentRoute.value = window.location.pathname
    // The page shown after going back carries its own title.
    showTitle(window.location.pathname)
  })
}

export function useCustomRouter() {
  listenToHistory()

  // Initialize route from current URL on page load
  if (_currentRoute.value === '/') {
    _currentRoute.value = window.location.pathname
  }

  return {
    navigateTo,
  }
}
